const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/triemli/eCheckin/sections/PersAngaben.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../common/translations';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { GenderField } from 'App/components/eCheckin/components/fields/metakat/GenderField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { useText } from '@cistec/common_web/utils/intlUtils';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { Alert } from 'App/components/Alert/Alert';
import _ from 'lodash';
import { Divider } from '@mui/material';

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;

    const title = useText(props.title);
    const persAngabenInfoText = useText(textMap.persAngabenInfoText);
    const eMailInfoText = useText(textMap.eMailInfo);
    const mobileNumberInfo = useText(textMap.mobileNumberInfo);
    const pafInfoText = useText(textMap.pafInfoText);

    const PATH_FIRST_NAME = createFieldPath(path, 'firstname');
    const PATH_LAST_NAME = createFieldPath(path, 'lastname');
    const PATH_DATE_OF_BIRTH = createFieldPath(path, 'dateOfBirth');
    const PATH_COUNTRY = createFieldPath(path, 'country');
    const PATH_EMAIL = createFieldPath(path, 'email');
    const PATH_HOMETOWN = createFieldPath(path, 'hometown');
    const PATH_PROFESSION = createFieldPath(path, 'profession');
    const PATH_TEL = createFieldPath(path, 'tel');
    const PATH_MOBILE = createFieldPath(path, 'mobile');
    const PATH_GENDER = createFieldPath(path, 'gender');
    const PATH_NATIONALITY = createFieldPath(path, 'nationality');
    const PATH_NO_AHV = createFieldPath(path, 'noAhv');
    const PATH_AHV = createFieldPath(path, 'ahv');
    const PATH_CIVIL_STATUS = createFieldPath(path, 'civilStatus');
    const PATH_RELIGION = createFieldPath(path, 'religion');
    const PATH_OTHER_RELIGION = createFieldPath(path, 'otherReligion');
    const PATH_PAF = createFieldPath(path, 'paf');
    const PATH_PAF_LASTNAME = createFieldPath(path, 'pafLastname');
    const PATH_PAF_FIRSTNAME = createFieldPath(path, 'pafFirstname');
    const PATH_PAF_ADDRESSE = createFieldPath(path, 'pafAddress');
    const PATH_PAF_TEL = createFieldPath(path, 'pafTel');
    const PATH_PAF_EMAIL = createFieldPath(path, 'pafEmail');
    const PATH_EPD = createFieldPath(path, 'epd');

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const isNoAHV = _.get(formData, PATH_NO_AHV) === '1';
    const isSwiss = _.get(formData, PATH_NATIONALITY) === 'CH';
    const hasPaf = _.get(formData, PATH_PAF) === '1';
    const isOtherReligion = _.get(formData, PATH_RELIGION) === 'other';

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <Alert color={'info'} hideIcon>
                {persAngabenInfoText}
            </Alert>
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required />
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required />
            <GenderField path={PATH_GENDER} label={textMap.gender} required />
            <BirthDateField label={textMap.dateOfBirth} path={PATH_DATE_OF_BIRTH} required />
            <Divider />
            <CIS4mSingleCheckbox label={textMap.noAHV} path={PATH_NO_AHV} />
            <AHVField label={textMap.ahv} path={PATH_AHV} hidden={isNoAHV} required />
            <CountryField label={textMap.nationality} path={PATH_NATIONALITY} required />
            <CIS4mTextField label={textMap.hometown} path={PATH_HOMETOWN} hidden={!isSwiss} required />
            <CIS4mTextField label={textMap.profession} path={PATH_PROFESSION} required />

            <PhoneNumberField label={textMap.tel} path={PATH_TEL} countryCodePath={PATH_COUNTRY} />
            <PhoneNumberField
                label={textMap.telephoneMobile}
                path={PATH_MOBILE}
                countryCodePath={PATH_COUNTRY}
                required
            />
            <Alert color={'info'} hideIcon>
                {mobileNumberInfo}
            </Alert>

            <EMailField label={textMapCommon.email} path={PATH_EMAIL} required />
            <Alert color={'info'} hideIcon>
                {eMailInfoText}
            </Alert>
            <Divider />

            <ReligionField label={textMap.religion} path={PATH_RELIGION} required />
            <CIS4mTextField
                label={textMap.religion_other}
                path={PATH_OTHER_RELIGION}
                hidden={!isOtherReligion}
                required
            />

            <ZivilstandField label={textMap.zivilstand} path={PATH_CIVIL_STATUS} required />

            <YesNoField label={textMap.paf} path={PATH_PAF} required />
            <CIS4mBlock hidden={!hasPaf}>
                <Alert color={'info'} hideIcon>
                    {pafInfoText}
                </Alert>
                <div>{useText(textMap.representative)}</div>

                <CIS4mTextField label={textMapCommon.lastname} path={PATH_PAF_LASTNAME} required />
                <CIS4mTextField label={textMapCommon.firstname} path={PATH_PAF_FIRSTNAME} required />
                <CIS4mTextField label={textMap.address} path={PATH_PAF_ADDRESSE} />
                <PhoneNumberField label={textMapCommon.mobile} path={PATH_PAF_TEL} />
                <EMailField label={textMapCommon.email} path={PATH_PAF_EMAIL} />
            </CIS4mBlock>
            <YesNoField label={textMap.epd} path={PATH_EPD} required />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
